import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Api } from '../../auth/Configs';
import {
  ARCHIVE_PROFILES,
  GED_DOCUMENT_IMPORT_ZIP,
  GED_DOCUMENT_VALIDATE_BATCH,
  GED_DOCUMENT_IMPORT_ZIP_PREVIEW,
  PLAN_FOLDERS,
  PLAN_META_LIST,
  REGLES_CONSERVATION,
} from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';
import './ArchiveStyles.css';
import './ArchiveCreate.css';
import './ArchiveImport.css';
import ImportMetadataReport from './ImportMetadataReport';
import { getAssignedServiceIds } from './serviceScope';
import ImportPreparationFields, { preparationStatusLabels } from './ImportPreparationFields';
import useImportTaskAutosave from './useImportTaskAutosave';
import { rowsWithDraft, taskRowPatches, taskDate } from './importTaskHelpers';

const defaultForm = {
  planId: '',
  folderId: '',
  archiveProfileId: '',
  regleConservationId: '',
  producerServiceId: '',
};

const importErrorMessage = (error, fallback) => {
  const payload = error.response?.data;
  const message = typeof payload?.message === 'string' ? payload.message.trim() : '';
  const details = Array.isArray(payload?.errors) ? payload.errors.map((item) =>
    typeof item === 'string' ? item : [item.file, item.message].filter(Boolean).join(' : ')
  ).filter(Boolean).join(' ; ') : '';
  return [message, details].filter(Boolean).join(' — ') || fallback;
};

const normalizePreparedItems = (items) => (items || []).map((item) => ({
  ...item,
  title: item.title || (item.basename ? item.basename.replace(/\.[^/.]+$/, '') : ''),
  reference: item.reference ?? '',
  errors: Array.isArray(item.errors) ? item.errors : [],
  planId: item.planId ? String(item.planId) : '',
  folderId: item.folderId ? String(item.folderId) : '',
  archiveProfileId: item.archiveProfileId ? String(item.archiveProfileId) : '',
  regleConservationId: item.regleConservationId ? String(item.regleConservationId) : '',
  producerServiceId: item.producerServiceId ? String(item.producerServiceId) : '',
  metadata: item.metadata && !Array.isArray(item.metadata) ? item.metadata : {},
  metadataJson: JSON.stringify(item.metadata && !Array.isArray(item.metadata) ? item.metadata : {}),
  metadataError: '',
}));

const flattenServices = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      label: `${'-- '.repeat(depth)}${node.code} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenServices(node.children, depth + 1));
    }
    return acc;
  }, []);

const flattenFolders = (nodes, depth = 0, ancestorIds = []) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      planId: node.planId || null,
      ancestorIds,
      label: `${'-- '.repeat(depth)}${node.reference} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenFolders(node.children, depth + 1, [...ancestorIds, Number(node.id)]));
    }
    return acc;
  }, []);

const sortPlans = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  if (codeA && codeB && codeA !== codeB) {
    return codeA.localeCompare(codeB);
  }
  return (a.title || '').toString().localeCompare((b.title || '').toString());
};

const buildPlanTree = (plans) => {
  const byId = new Map();
  (plans || []).forEach((plan) => {
    byId.set(Number(plan.id), { ...plan, children: [] });
  });
  const roots = [];
  byId.forEach((node) => {
    const parentId = node.parentId ? Number(node.parentId) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortNode = (node) => {
    node.children.sort(sortPlans);
    node.children.forEach(sortNode);
  };
  roots.sort(sortPlans);
  roots.forEach(sortNode);
  return roots;
};

const flattenPlanTree = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({ plan: node, depth });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenPlanTree(node.children, depth + 1));
    }
    return acc;
  }, []);

const slugifyCode = (value) =>
  (value || '')
    .toString()
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '')
    .replace(/_+/g, '_');

const MAPPING_CSV_BASE_HEADERS = [
  'file',
  'title',
  'reference',
  'planId',
  'planCode',
  'planTitle',
  'folderId',
  'folderLabel',
];

const MAPPING_CSV_ALIASES = {
  file: ['file', 'filename', 'fichier'],
  title: ['title', 'titre'],
  reference: ['reference', 'ref'],
  planId: ['planid', 'plan_id'],
  planCode: ['plancode', 'plan_code', 'plan'],
  planTitle: ['plantitle', 'plan_title', 'planlabel', 'plan_label'],
  folderId: ['folderid', 'folder_id', 'dossierid', 'dossier_id'],
  folderLabel: ['folderlabel', 'folder_label', 'dossier', 'dossierlabel', 'dossier_label'],
  metadataJson: ['metadatajson', 'metadata_json', 'metadata'],
};

const normalizeCsvValue = (value) => (value || '').toString().trim().toLowerCase();

const formatMetadataValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (raw === '') return '';
  const needsQuotes = /[",\n\r]/.test(raw);
  const escaped = raw.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
};

const detectCsvDelimiter = (text) => {
  const firstLine = (text || '').split(/\r?\n/)[0] || '';
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i += 1) {
    const char = firstLine[i];
    if (char === '"') {
      if (inQuotes && firstLine[i + 1] === '"') {
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (char === ',') commas += 1;
    if (char === ';') semicolons += 1;
  }
  return semicolons > commas ? ';' : ',';
};

const parseCsvText = (text, delimiter) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const source = (text || '').replace(/^\uFEFF/, '');
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    if (inQuotes) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === delimiter) {
      row.push(field);
      field = '';
      continue;
    }
    if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      continue;
    }
    if (char === '\r') {
      continue;
    }
    field += char;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
};

const getFileBasename = (value) => {
  const normalized = (value || '').toString().trim();
  if (!normalized) return '';
  const parts = normalized.split(/[\\/]/);
  return parts[parts.length - 1] || '';
};

const ArchiveImport = () => {
  const session = useSelector((state) => state.sessionAuth);
  const profile = useSelector((state) => state.userSettings);
  const navigate = useNavigate();
  const location = useLocation();
  const csvInputRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [folders, setFolders] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [regles, setRegles] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [addingService, setAddingService] = useState(false);
  const [newService, setNewService] = useState({ code: '', title: '' });
  const [form, setForm] = useState(defaultForm);
  const [useFolder, setUseFolder] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [batchValidation, setBatchValidation] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [previewRequestError, setPreviewRequestError] = useState('');
  const [importRequestError, setImportRequestError] = useState('');
  const [preparation, setPreparation] = useState(null);
  const [preparationDirty, setPreparationDirty] = useState(false);
  const [savedPreparationSettings, setSavedPreparationSettings] = useState('');
  const [lotTitle, setLotTitle] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [leaving, setLeaving] = useState(false);
  const [draftReset, setDraftReset] = useState(0);
  const latestDraftRef = useRef(null);
  const preparationRef = useRef(null);
  const preparationRequest = useRef(0);
  const [showMetadata, setShowMetadata] = useState(false);
  const [showOcr, setShowOcr] = useState(true);
  const [completeMetadata, setCompleteMetadata] = useState(true);
  const [verifyMetadata, setVerifyMetadata] = useState(false);
  const updateMetadataReport = useCallback((data) => setResult((previous) => previous ? { ...previous, created: data.created } : previous), []);
  const [metadataDefaults, setMetadataDefaults] = useState([{ key: '', value: '' }]);
  const preparationSettings = JSON.stringify([form, useFolder, completeMetadata, verifyMetadata, metadataDefaults]);
  const preparationPending = (preparation?.counts?.queued || 0) + (preparation?.counts?.processing || 0);
  const unsavedPreparation = preparationDirty || savedPreparationSettings !== preparationSettings;
  const taskEditable = preparation && ['preparing', 'paused'].includes(preparation.status);
  const preparationLocked = previewLoading || importing || leaving || (preparation && !taskEditable);

  const roles = Array.isArray(session.roles) ? session.roles : [];
  const isAssistantOnly =
    roles.includes('ROLE_ASSIST')
    && !roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
    );
  const canImport = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
  );
  const canValidate = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
  );
  const canAddService = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
  );

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedPlanId = useMemo(() => {
    const value = queryParams.get('planId');
    return value ? Number(value) : null;
  }, [queryParams]);
  const requestedFolderId = useMemo(() => {
    const value = queryParams.get('folderId');
    return value ? Number(value) : null;
  }, [queryParams]);

  const accessFilters = profile?.accessFilters || {};
  const planScopeIds = useMemo(
    () => (Array.isArray(accessFilters.planIds) ? accessFilters.planIds.map(Number) : []),
    [accessFilters.planIds]
  );
  const folderScopeIds = useMemo(
    () => (Array.isArray(accessFilters.folderIds) ? accessFilters.folderIds.map(Number) : []),
    [accessFilters.folderIds]
  );
  const serviceScopeIds = useMemo(() => getAssignedServiceIds(profile), [profile]);
  const hasPlanScope = planScopeIds.length > 0;
  const hasFolderScope = folderScopeIds.length > 0;
  const userServiceId = profile?.serviceUnit?.id ? String(profile.serviceUnit.id) : '';

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const buildTemplateCsv = () => {
    const defaultKeys = Array.from(
      new Set(
        metadataDefaults
          .map((field) => (typeof field?.key === 'string' ? field.key.trim() : ''))
          .filter((key) => key !== '')
      )
    );
    const metadataKeys = defaultKeys.length > 0 ? defaultKeys : ['Matricule', 'option'];
    const header = [...MAPPING_CSV_BASE_HEADERS, ...metadataKeys];
    const baseSample = [
      'fichier.pdf',
      'Rapport 2025',
      'REF-0001',
      '1',
      'PLAN_CODE',
      'Plan exemple',
      '10',
      'Dossier exemple',
    ];
    const metadataSample = metadataKeys.map((key) => {
      const match = metadataDefaults.find(
        (field) => typeof field?.key === 'string' && field.key.trim() === key
      );
      if (!match || match.value === null || match.value === undefined) {
        return '';
      }
      return typeof match.value === 'string' ? match.value.trim() : String(match.value);
    });
    const sample = [...baseSample, ...metadataSample];
    return `${header.join(',')}\n${sample.join(',')}\n`;
  };

  const downloadManifestTemplate = () => {
    const csv = buildTemplateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'manifest.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const parseMetadataJson = (value) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (err) {
      return { __error: 'JSON invalide' };
    }
    return { __error: 'JSON invalide' };
  };

  const formatOcrValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderOcrMetadata = (ocrMetadata) => {
    if (!ocrMetadata || typeof ocrMetadata !== 'object') {
      return <span className="text-muted">—</span>;
    }
    const entries = Object.entries(ocrMetadata).filter(([, value]) => value !== null && value !== '');
    if (entries.length === 0) {
      return <span className="text-muted">—</span>;
    }
    return (
      <div className="archive-ocr-preview">
        {entries.map(([key, value]) => (
          <div className="archive-ocr-row" key={key}>
            <strong>{key}</strong>: {formatOcrValue(value)}
          </div>
        ))}
      </div>
    );
  };

  const updateMetadataDefault = (index, field, value) => {
    setMetadataDefaults((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addMetadataDefault = () => {
    setMetadataDefaults((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeMetadataDefault = (index) => {
    setMetadataDefaults((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const buildMetadataDefaults = () => {
    if (!Array.isArray(metadataDefaults) || metadataDefaults.length === 0) {
      return null;
    }
    const metadata = {};
    metadataDefaults.forEach((field) => {
      const key = typeof field?.key === 'string' ? field.key.trim() : '';
      if (!key) return;
      const rawValue = field?.value;
      if (rawValue === null || rawValue === undefined) return;
      const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
      if (value === '') return;
      metadata[key] = value;
    });
    return Object.keys(metadata).length > 0 ? metadata : null;
  };

  const mergeMetadataDefaults = (metadata) => {
    const defaults = buildMetadataDefaults() || {};
    const base = metadata && typeof metadata === 'object' ? metadata : {};
    return { ...defaults, ...base };
  };

  const createService = async () => {
    const title = newService.title.trim();
    const code = newService.code.trim() || slugifyCode(title);
    if (!title) {
      throw new Error('Libellé du service requis.');
    }
    if (!code) {
      throw new Error('Code du service requis.');
    }
    const response = await axios.post(
      `${Api}/services`,
      { code, title },
      authHeaders
    );
    const createdId = response.data?.id ? String(response.data.id) : '';
    const listRes = await axios.get(`${Api}/services`, authHeaders);
    setServices(flattenServices(listRes.data?.services || []));
    setNewService({ code: '', title: '' });
    setAddingService(false);
    if (createdId) {
      setForm((prev) => ({ ...prev, producerServiceId: createdId }));
    }
    return createdId;
  };

  const buildOverridesPayload = (items) =>
    items.map((item) => {
      const parsedMetadata = parseMetadataJson(item.metadataJson || '');
      const metadata =
        parsedMetadata && !parsedMetadata.__error ? parsedMetadata : undefined;
      return {
        file: item.file,
        title: item.title,
        reference: item.reference ?? '',
        planId: item.planId || '',
        folderId: item.folderId || '',
        archiveProfileId: item.archiveProfileId || '',
        regleConservationId: item.regleConservationId || '',
        producerServiceId: item.producerServiceId || '',
        metadata,
      };
    });

  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    try { await autosave.flush(); } catch (_) { return; }
    ++preparationRequest.current;
    preparationRef.current = null;
    setPreparation(null);
    setPreviewLoading(false);
    setPreviewItems([]);
    setPreviewErrors([]);
    setPreviewRequestError('');
    setImportRequestError('');
    setResult(null);
    setPreparationDirty(false);
    setSavedPreparationSettings('');
    setLotTitle('');
    setSelectedFiles([]);
    const params = new URLSearchParams(location.search);
    params.delete('preparationId');
    params.delete('batchId');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setZipFile(acceptedFiles[0] || null);
    handlePreview(false, false, acceptedFiles[0]);
  };

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: { 'application/zip': ['.zip'] },
    onDrop,
    disabled: importing || previewLoading || leaving,
  });

  const applyPreparation = useCallback((payload, keepLocal = false) => {
    preparationRef.current = payload;
    setPreparation(payload);
    const patches = keepLocal ? latestDraftRef.current?.rows || {} : payload.draft?.rows || {};
    setPreviewItems(rowsWithDraft(normalizePreparedItems(payload.files), patches));
    setPreviewErrors(payload.errors || []);
    if (!keepLocal) {
      setLotTitle(payload.draft?.editor?.title ?? payload.title ?? '');
      setDraftReset((value) => value + 1);
    }
  }, []);

  const restorePreparation = useCallback((data) => {
    const options = { ...(data.options || {}), ...(data.draft?.options || {}) };
    const editor = data.draft?.editor || {};
    const restoredForm = editor.form || Object.fromEntries(Object.keys(defaultForm).map((key) => [key, options[key] ? String(options[key]) : '']));
    const restoredFolder = editor.useFolder ?? (options.useFolder === true || options.useFolder === '1');
    const restoredComplete = editor.completeMetadata ?? (options.completeMetadata !== false && options.completeMetadata !== '0');
    const restoredVerify = editor.verifyMetadata ?? (options.verifyMetadata === true || options.verifyMetadata === '1');
    let defaults = {};
    try { defaults = JSON.parse(options.metadataDefaults || '{}'); } catch (_) { /* Use the saved raw editor values below when available. */ }
    const restoredDefaults = editor.metadataDefaults || (Object.keys(defaults).length ? Object.entries(defaults).map(([key, value]) => ({ key, value })) : [{ key: '', value: '' }]);
    setForm(restoredForm); setUseFolder(restoredFolder); setCompleteMetadata(restoredComplete); setVerifyMetadata(restoredVerify); setMetadataDefaults(restoredDefaults);
    setSavedPreparationSettings(JSON.stringify([restoredForm, restoredFolder, restoredComplete, restoredVerify, restoredDefaults]));
    setPreparationDirty(false);
    applyPreparation(data);
    if (data.importResult) setResult(data.importResult);
  }, [applyPreparation]);

  const taskDraft = {
    title: lotTitle,
    rows: taskRowPatches(previewItems, preparation?.files),
    options: { ...form, useFolder, completeMetadata, verifyMetadata, metadataDefaults: JSON.stringify(buildMetadataDefaults() || {}) },
    editor: { title: lotTitle, form, useFolder, completeMetadata, verifyMetadata, metadataDefaults },
  };
  latestDraftRef.current = taskDraft;
  const autosave = useImportTaskAutosave({
    id: preparation?.preparationId, draft: taskDraft, token: session.token,
    enabled: Boolean(taskEditable) && !previewLoading && !importing,
    resetKey: draftReset, getEditRevision: () => preparationRef.current?.editRevision ?? 0,
    onSaved: (data) => applyPreparation(data, true),
  });
  const canImportPreparation = preparation?.ready && !unsavedPreparation && !preparationLocked && !autosave.dirty && !autosave.saving;
  const navigationRef = useRef(null);
  navigationRef.current = { guard: taskEditable && (autosave.dirty || autosave.saving), leave: async (to) => {
    setLeaving(true);
    try { await autosave.flush(); navigate(to); } catch (_) { setLeaving(false); }
  } };

  useEffect(() => {
    const protectNavigation = (event) => {
      if (!navigationRef.current.guard || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      event.preventDefault(); event.stopPropagation();
      navigationRef.current.leave(url.pathname + url.search + url.hash);
    };
    document.addEventListener('click', protectNavigation, true);
    return () => document.removeEventListener('click', protectNavigation, true);
  }, []);

  const requestedPreparationId = queryParams.get('preparationId');
  useEffect(() => {
    if (!requestedPreparationId || preparationRef.current?.preparationId === requestedPreparationId) return undefined;
    let active = true;
    const generation = ++preparationRequest.current;
    setPreviewLoading(true);
    axios.get(`${GED_DOCUMENT_IMPORT_ZIP_PREVIEW()}/${encodeURIComponent(requestedPreparationId)}`, authHeaders)
      .then(({ data }) => {
        if (!active || generation !== preparationRequest.current) return;
        restorePreparation(data);
      })
      .catch((error) => {
        if (active && generation === preparationRequest.current) setPreviewRequestError(importErrorMessage(error, 'Préparation indisponible.'));
      })
      .finally(() => { if (active && generation === preparationRequest.current) setPreviewLoading(false); });
    return () => { active = false; };
  }, [requestedPreparationId, authHeaders, restorePreparation]);

  useEffect(() => {
    const running = preparation?.status === 'importing' || (preparation?.status === 'preparing' && preparationPending > 0)
      || (preparation?.status === 'paused' && preparation.counts?.processing > 0);
    if (!preparation?.preparationId || !running || previewLoading) return undefined;
    let active = true;
    const generation = preparationRequest.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${GED_DOCUMENT_IMPORT_ZIP_PREVIEW()}/${preparation.preparationId}`, authHeaders);
        if (active && generation === preparationRequest.current) {
          applyPreparation(data, true);
          if (data.importResult) setResult(data.importResult);
          setPreviewRequestError('');
        }
      } catch (error) {
        if (active && generation === preparationRequest.current) {
          setPreviewRequestError(importErrorMessage(error, 'Suivi de l’analyse indisponible. Cliquez sur Actualiser.'));
        }
      }
    }, 2500);
    return () => { active = false; clearTimeout(timer); };
  }, [preparation, preparationPending, previewLoading, authHeaders, applyPreparation]);

  useEffect(() => () => { ++preparationRequest.current; }, []);

  useEffect(() => {
    axios
      .get(ARCHIVE_PROFILES(), {
        ...authHeaders,
        params: { status: 'active', available: 1, limit: 100 },
      })
      .then((res) => {
        setProfiles(res.data?.profiles || []);
      })
      .catch(() => setProfiles([]));
  }, [authHeaders]);

  useEffect(() => {
    axios
      .get(REGLES_CONSERVATION(), authHeaders)
      .then((res) => {
        setRegles(res.data?.regles || []);
      })
      .catch(() => setRegles([]));
  }, [authHeaders]);

  useEffect(() => {
    setLoadingServices(true);
    axios
      .get(`${Api}/services`, authHeaders)
      .then((res) => {
        const flat = flattenServices(res.data?.services || []);
        setServices(flat);
      })
      .catch(() => setServices([]))
      .finally(() => setLoadingServices(false));
  }, [authHeaders]);

  useEffect(() => {
    if (isAssistantOnly && userServiceId && serviceScopeIds.length === 1) {
      setForm((prev) => ({ ...prev, producerServiceId: userServiceId }));
    }
  }, [isAssistantOnly, userServiceId, serviceScopeIds]);

  useEffect(() => {
    setLoadingPlans(true);
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        setPlans(res.data?.plans || []);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoadingPlans(false));
  }, [authHeaders]);

  useEffect(() => {
    setLoadingFolders(true);
    axios
      .get(PLAN_FOLDERS(), authHeaders)
      .then((res) => {
        const flat = flattenFolders(res.data?.folders || []);
        setFolders(flat);
      })
      .catch(() => setFolders([]))
      .finally(() => setLoadingFolders(false));
  }, [authHeaders]);

  const scopedFolders = useMemo(
    () =>
      folders.filter(
        (folder) =>
          (!hasPlanScope || planScopeIds.includes(Number(folder.planId))) &&
          (!hasFolderScope || folderScopeIds.includes(Number(folder.id)) ||
            folder.ancestorIds.some((id) => folderScopeIds.includes(id)))
      ),
    [folders, hasPlanScope, planScopeIds, hasFolderScope, folderScopeIds]
  );

  const availablePlans = useMemo(() => {
    let list = plans.filter((plan) => plan.active);
    if (hasPlanScope) {
      list = list.filter((plan) => planScopeIds.includes(Number(plan.id)));
    }
    if (hasFolderScope) {
      const allowedPlanIds = new Set(scopedFolders.map((folder) => Number(folder.planId)));
      if (allowedPlanIds.size > 0) {
        list = list.filter((plan) => allowedPlanIds.has(Number(plan.id)));
      } else {
        list = [];
      }
    }
    return list;
  }, [
    plans,
    scopedFolders,
    hasPlanScope,
    planScopeIds,
    hasFolderScope,
  ]);

  const groupedPlanOptions = useMemo(
    () => flattenPlanTree(buildPlanTree(availablePlans)),
    [availablePlans]
  );

  const filteredFolders = useMemo(() => {
    if (!form.planId) return [];
    return scopedFolders.filter((folder) => Number(folder.planId) === Number(form.planId));
  }, [scopedFolders, form.planId]);

  const foldersByPlan = useMemo(() => {
    const map = {};
    scopedFolders.forEach((folder) => {
      const key = String(folder.planId || '');
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(folder);
    });
    return map;
  }, [scopedFolders]);

  const planById = useMemo(() => {
    const map = new Map();
    availablePlans.forEach((plan) => {
      map.set(String(plan.id), plan);
    });
    return map;
  }, [availablePlans]);

  const planByCode = useMemo(() => {
    const map = new Map();
    availablePlans.forEach((plan) => {
      map.set(normalizeCsvValue(plan.code), plan);
    });
    return map;
  }, [availablePlans]);

  const planByTitle = useMemo(() => {
    const map = new Map();
    availablePlans.forEach((plan) => {
      map.set(normalizeCsvValue(plan.title), plan);
    });
    return map;
  }, [availablePlans]);

  const folderById = useMemo(() => {
    const map = new Map();
    scopedFolders.forEach((folder) => {
      map.set(String(folder.id), folder);
    });
    return map;
  }, [scopedFolders]);

  const folderByLabel = useMemo(() => {
    const map = new Map();
    scopedFolders.forEach((folder) => {
      map.set(normalizeCsvValue(folder.label), folder);
    });
    return map;
  }, [scopedFolders]);

  const duplicateReferences = useMemo(() => {
    const counts = {};
    previewItems.forEach((item) => {
      const ref = (item.reference || '').trim();
      if (!ref) return;
      counts[ref] = (counts[ref] || 0) + 1;
    });
    return new Set(
      Object.entries(counts)
        .filter(([, count]) => count > 1)
        .map(([ref]) => ref)
    );
  }, [previewItems]);

  const getMetadataCsvKeys = (items) => {
    const keys = new Set();
    items.forEach((item) => {
      const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : null;
      if (!metadata) return;
      Object.keys(metadata).forEach((key) => {
        if (key) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  };

  const buildMappingCsv = () => {
    const lines = [];
    const metadataKeys = getMetadataCsvKeys(previewItems);
    const headers = [...MAPPING_CSV_BASE_HEADERS, ...metadataKeys];
    lines.push(headers.map(escapeCsvValue).join(','));
    previewItems.forEach((item) => {
      const plan = planById.get(String(item.planId));
      const folder = folderById.get(String(item.folderId));
      const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
      const row = [
        item.file || '',
        item.title || '',
        item.reference || '',
        item.planId || '',
        plan?.code || '',
        plan?.title || '',
        item.folderId || '',
        folder?.label || '',
        ...metadataKeys.map((key) => formatMetadataValue(metadata[key])),
      ];
      lines.push(row.map(escapeCsvValue).join(','));
    });
    return lines.join('\n');
  };

  const handleExportMappingCsv = () => {
    if (previewItems.length === 0) {
      ToastError('Aucun mapping à exporter.');
      return;
    }
    const csv = buildMappingCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'manifest.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleImportMappingCsv = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (previewItems.length === 0) {
      ToastError('Aucun mapping à mettre à jour.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const text = String(loadEvent.target?.result || '');
      const delimiter = detectCsvDelimiter(text);
      const rows = parseCsvText(text, delimiter);
      if (rows.length === 0) {
        ToastError('Fichier CSV vide.');
        return;
      }
      const headerRow = rows.shift() || [];
      const headerIndex = {};
      headerRow.forEach((header, index) => {
        const normalized = normalizeCsvValue(header);
        Object.entries(MAPPING_CSV_ALIASES).forEach(([key, aliases]) => {
          if (!aliases.includes(normalized) || headerIndex[key] !== undefined) return;
          headerIndex[key] = index;
        });
      });
      if (headerIndex.file === undefined) {
        ToastError('Colonne fichier manquante (file/filename/fichier).');
        return;
      }
      const mappedIndexes = new Set(Object.values(headerIndex));
      const metadataColumns = [];
      const metadataKeys = new Set();
      headerRow.forEach((header, index) => {
        if (mappedIndexes.has(index)) return;
        const rawHeader = typeof header === 'string' ? header.trim() : String(header || '').trim();
        if (!rawHeader) return;
        let metaKey = rawHeader;
        const lower = rawHeader.toLowerCase();
        if (lower.startsWith('metadata.') || lower.startsWith('meta.')) {
          metaKey = rawHeader.split('.', 2)[1] || '';
        }
        metaKey = metaKey.trim();
        if (!metaKey || metadataKeys.has(metaKey)) return;
        metadataKeys.add(metaKey);
        metadataColumns.push({ index, key: metaKey });
      });

      const fileIndex = new Map();
      const basenameCounts = new Map();
      previewItems.forEach((item, index) => {
        const fileKey = (item.file || '').trim();
        if (fileKey) {
          fileIndex.set(fileKey, index);
          const base = getFileBasename(fileKey);
          basenameCounts.set(base, (basenameCounts.get(base) || 0) + 1);
        }
      });
      const basenameIndex = new Map();
      previewItems.forEach((item, index) => {
        const base = getFileBasename(item.file || '');
        if (base && basenameCounts.get(base) === 1) {
          basenameIndex.set(base, index);
        }
      });

      const updatesByIndex = new Map();
      let matchedRows = 0;
      let missingRows = 0;
      let invalidLookups = 0;

      const getValue = (row, key) => {
        if (headerIndex[key] === undefined) return undefined;
        const raw = row[headerIndex[key]] ?? '';
        return typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
      };

      rows.forEach((row) => {
        if (!row || row.length === 0) return;
        const fileValue = getValue(row, 'file');
        if (!fileValue) return;
        const normalizedFile = fileValue.trim();
        let targetIndex = fileIndex.get(normalizedFile);
        if (targetIndex === undefined) {
          const base = getFileBasename(normalizedFile);
          targetIndex = basenameIndex.get(base);
        }
        if (targetIndex === undefined) {
          missingRows += 1;
          return;
        }

        const current = previewItems[targetIndex];
        const updates = {};

        if (headerIndex.title !== undefined) {
          updates.title = getValue(row, 'title') || '';
        }
        if (headerIndex.reference !== undefined) {
          updates.reference = getValue(row, 'reference') || '';
        }

        const hasPlanData = headerIndex.planId !== undefined
          || headerIndex.planCode !== undefined
          || headerIndex.planTitle !== undefined;
        if (hasPlanData) {
          const rawPlanId = getValue(row, 'planId');
          let resolvedPlanId = '';
          if (rawPlanId) {
            if (planById.has(rawPlanId)) {
              resolvedPlanId = rawPlanId;
            } else {
              invalidLookups += 1;
            }
          }
          if (!resolvedPlanId) {
            const planCode = getValue(row, 'planCode');
            const planTitle = getValue(row, 'planTitle');
            const resolved =
              planByCode.get(normalizeCsvValue(planCode))
              || planByTitle.get(normalizeCsvValue(planTitle));
            if (resolved) {
              resolvedPlanId = String(resolved.id);
            } else if (planCode || planTitle) {
              invalidLookups += 1;
            }
          }
          const canClearPlan = rawPlanId !== undefined && rawPlanId === '';
          if (resolvedPlanId || canClearPlan) {
            updates.planId = resolvedPlanId || '';
          }
        }

        const hasFolderData = headerIndex.folderId !== undefined
          || headerIndex.folderLabel !== undefined;
        if (useFolder && hasFolderData) {
          const rawFolderId = getValue(row, 'folderId');
          let resolvedFolderId = '';
          if (rawFolderId) {
            if (folderById.has(rawFolderId)) {
              resolvedFolderId = rawFolderId;
            } else {
              invalidLookups += 1;
            }
          }
          if (!resolvedFolderId) {
            const folderLabel = getValue(row, 'folderLabel');
            const resolved =
              folderByLabel.get(normalizeCsvValue(folderLabel));
            if (resolved) {
              resolvedFolderId = String(resolved.id);
            } else if (folderLabel) {
              invalidLookups += 1;
            }
          }
          const canClearFolder = rawFolderId !== undefined && rawFolderId === '';
          if (resolvedFolderId || canClearFolder) {
            updates.folderId = resolvedFolderId || '';
          }
        }

        const metadataUpdates = {};
        metadataColumns.forEach(({ index, key }) => {
          const raw = row[index] ?? '';
          const value = typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
          if (value === '') return;
          metadataUpdates[key] = value;
        });

        if (headerIndex.metadataJson !== undefined || Object.keys(metadataUpdates).length > 0) {
          let metadataValue = '';
          let parsedMetadata = null;
          let metadataError = '';
          if (headerIndex.metadataJson !== undefined) {
            const rawValue = row[headerIndex.metadataJson] ?? '';
            metadataValue = typeof rawValue === 'string' ? rawValue.trim() : String(rawValue || '').trim();
            const parsed = parseMetadataJson(metadataValue);
            if (parsed && parsed.__error) {
              metadataError = parsed.__error;
            } else if (parsed) {
              parsedMetadata = parsed;
            } else {
              parsedMetadata = {};
            }
          }

          if (metadataError) {
            updates.metadata = mergeMetadataDefaults(metadataUpdates);
            updates.metadataJson = metadataValue;
            updates.metadataError = metadataError;
          } else {
            const combined = { ...(parsedMetadata || {}), ...metadataUpdates };
            const merged = mergeMetadataDefaults(combined);
            updates.metadata = merged;
            updates.metadataJson = Object.keys(merged).length > 0
              ? JSON.stringify(merged)
              : '';
            updates.metadataError = '';
          }
        }

        if (updates.planId !== undefined && updates.planId !== current.planId && updates.folderId === undefined) {
          updates.folderId = '';
        }
        if (!useFolder && updates.folderId !== undefined) {
          updates.folderId = '';
        }
        if (isAssistantOnly && userServiceId && serviceScopeIds.length === 1) {
          updates.producerServiceId = userServiceId;
        }

        updatesByIndex.set(targetIndex, { ...(updatesByIndex.get(targetIndex) || {}), ...updates });
        matchedRows += 1;
      });

      if (updatesByIndex.size === 0) {
        ToastError('Aucune ligne du CSV ne correspond aux fichiers prévisualisés.');
        return;
      }

      setPreviewItems((prev) =>
        prev.map((item, index) => {
          const updates = updatesByIndex.get(index);
          return updates ? { ...item, ...updates } : item;
        })
      );

      setPreparationDirty(true);

      ToastConfirm(`Mapping CSV importé (${matchedRows} ligne${matchedRows > 1 ? 's' : ''} traitée${matchedRows > 1 ? 's' : ''}).`);
      if (missingRows > 0) {
        ToastError(`${missingRows} ligne${missingRows > 1 ? 's' : ''} ignorée${missingRows > 1 ? 's' : ''} (fichier introuvable).`);
      }
      if (invalidLookups > 0) {
        ToastError(`${invalidLookups} valeur${invalidLookups > 1 ? 's' : ''} non reconnue${invalidLookups > 1 ? 's' : ''} dans les colonnes de mapping.`);
      }
    };
    reader.onerror = () => {
      ToastError('Impossible de lire le fichier CSV.');
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    if (availablePlans.length === 0) {
      setForm((prev) => ({ ...prev, planId: '', folderId: '' }));
      return;
    }
    const allowed = availablePlans.some(
      (plan) => Number(plan.id) === Number(form.planId)
    );
    if (!form.planId || !allowed) {
      const requestedAllowed = requestedPlanId
        && availablePlans.some((plan) => Number(plan.id) === Number(requestedPlanId));
      const nextPlanId = requestedAllowed ? requestedPlanId : availablePlans[0].id;
      setForm((prev) => ({ ...prev, planId: nextPlanId, folderId: '' }));
    }
  }, [availablePlans, form.planId, requestedPlanId]);

  useEffect(() => {
    if (!requestedFolderId || !form.planId) return;
    const match = filteredFolders.find(
      (folder) => Number(folder.id) === Number(requestedFolderId)
    );
    if (match) {
      setUseFolder(true);
      setForm((prev) => ({ ...prev, folderId: String(match.id) }));
    }
  }, [filteredFolders, form.planId, requestedFolderId]);

  useEffect(() => {
    if (!useFolder && form.folderId) {
      setForm((prev) => ({ ...prev, folderId: '' }));
    }
  }, [useFolder, form.folderId]);

  useEffect(() => {
    if (isAssistantOnly && userServiceId && serviceScopeIds.length === 1 && previewItems.length > 0) {
      setPreviewItems((prev) =>
        prev.map((item) => ({
          ...item,
          producerServiceId: userServiceId,
        }))
      );
    }
  }, [isAssistantOnly, userServiceId, serviceScopeIds, previewItems.length]);

  const updatePreviewItem = (index, updates) => {
    setPreparationDirty(true);
    setPreviewItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...updates } : item
      )
    );
  };

  const updatePreparedMetadata = (index, key, value) => {
    const item = previewItems[index];
    const metadata = { ...item.metadata, [key]: value };
    const metadataWarnings = { ...item.metadataWarnings };
    delete metadataWarnings[key];
    updatePreviewItem(index, {
      metadata, metadataJson: JSON.stringify(metadata), metadataError: '', metadataWarnings,
      provenance: { ...item.provenance, [key]: 'manual' },
    });
  };

  const handleItemChange = (index, field, value) => {
    const updates = { [field]: value };
    if (field === 'planId') {
      updates.folderId = '';
    }
    if (field === 'metadataJson') {
      const parsed = parseMetadataJson(value);
      updates.metadataError = parsed && parsed.__error ? parsed.__error : '';
      if (parsed && !parsed.__error) {
        updates.metadata = mergeMetadataDefaults(parsed);
      } else if (!parsed) {
        updates.metadata = mergeMetadataDefaults({});
      } else {
        updates.metadata = previewItems[index]?.metadata || {};
      }
    }
    updatePreviewItem(index, updates);
  };

  const hasInvalidMetadata = (items) =>
    items.some((item) => item.metadataError);

  const showPreviewError = (message) => {
    setPreviewRequestError(message);
    ToastError(message);
  };

  const showImportError = (message) => {
    setImportRequestError(message);
    ToastError(message);
  };

  const handlePreview = async (confirmMetadata = false, retryExtraction = false, newFile = null, reanalyze = false) => {
    setPreviewRequestError('');
    if (!canImport) return;
    const currentPreparation = newFile ? null : preparation;
    const file = newFile || zipFile;
    if (!file && !currentPreparation) {
      showPreviewError('Veuillez fournir un fichier ZIP.');
      return;
    }
    if (!newFile && addingService && !isAssistantOnly && !newService.title.trim()) {
      showPreviewError('Libellé du service requis.');
      return;
    }
    if (!newFile && hasInvalidMetadata(previewItems)) {
      showPreviewError('Corrigez les métadonnées JSON invalides avant validation.');
      return;
    }

    let resolvedServiceId = form.producerServiceId;
    if (isAssistantOnly && userServiceId && serviceScopeIds.length === 1) {
      resolvedServiceId = userServiceId;
    } else if (!newFile && addingService) {
      try {
        resolvedServiceId = await createService();
      } catch (err) {
        showPreviewError(err.message || 'Impossible de créer le service.');
        return;
      }
    }

    if (currentPreparation) {
      try { await autosave.flush(); } catch (_) { return; }
    }

    const data = new FormData();
    if (currentPreparation) {
      data.append('preparationId', currentPreparation.preparationId);
      data.append('editRevision', preparationRef.current.editRevision ?? 0);
    } else data.append('archiveZip', file);
    if (!newFile && lotTitle.trim()) data.append('lotTitle', lotTitle);
    data.append('prepareMetadata', '1');
    data.append('confirmMetadata', confirmMetadata ? '1' : '0');
    data.append('retryExtraction', retryExtraction ? '1' : '0');
    data.append('reanalyze', reanalyze ? '1' : '0');
    if (form.planId) data.append('planId', form.planId);
    if (useFolder && form.folderId) {
      data.append('folderId', form.folderId);
    }
    if (form.archiveProfileId) data.append('archiveProfileId', form.archiveProfileId);
    if (form.regleConservationId) data.append('regleConservationId', form.regleConservationId);
    if (resolvedServiceId) data.append('producerServiceId', resolvedServiceId);
    data.append('useFolder', useFolder ? '1' : '0');
    data.append('completeMetadata', completeMetadata ? '1' : '0');
    data.append('verifyMetadata', verifyMetadata ? '1' : '0');

    const metadataDefaultsPayload = buildMetadataDefaults();
    if (metadataDefaultsPayload) {
      data.append('metadataDefaults', JSON.stringify(metadataDefaultsPayload));
    }

    if (!newFile && previewItems.length > 0) {
      const overrides = buildOverridesPayload(previewItems);
      data.append('overrides', JSON.stringify(overrides));
    }

    setPreviewLoading(true);
    setPreviewErrors([]);
    setPreviewRequestError('');
    setResult(null);
    setBatchValidation(null);
    const generation = ++preparationRequest.current;
    const submittedSettings = JSON.stringify([{ ...form, producerServiceId: resolvedServiceId }, useFolder, completeMetadata, verifyMetadata, metadataDefaults]);
    try {
      const response = await axios.post(GED_DOCUMENT_IMPORT_ZIP_PREVIEW(), data, {
        headers: {
          Authorization: 'Bearer ' + session.token,
          'Content-Type': 'multipart/form-data',
        },
      });
      const payload = response.data || {};
      if (generation !== preparationRequest.current) {
        return;
      }
      applyPreparation(payload);
      setPreparationDirty(false);
      setSavedPreparationSettings(submittedSettings);
      const params = new URLSearchParams(location.search);
      params.set('preparationId', payload.preparationId);
      params.delete('batchId');
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      if (!(payload.files || []).length) {
        showPreviewError('Aucun fichier à prévisualiser.');
      }
    } catch (error) {
      if (generation !== preparationRequest.current) return;
      const message = importErrorMessage(error, 'Erreur pendant la prévalidation.');
      showPreviewError(message);
    } finally {
      if (generation === preparationRequest.current) setPreviewLoading(false);
    }
  };

  const confirmTaskFiles = async (files) => {
    if (!preparation || !files.length) return;
    try {
      await autosave.flush();
      setPreviewLoading(true);
      const { data } = await axios.post(`${Api}/ged/documents/import-zip/tasks/${preparation.preparationId}/confirm`, {
        files, editRevision: preparationRef.current.editRevision ?? 0,
      }, authHeaders);
      applyPreparation(data);
      setPreparationDirty(false);
      setSavedPreparationSettings(preparationSettings);
      setSelectedFiles([]);
      setPreviewRequestError('');
    } catch (error) {
      showPreviewError(importErrorMessage(error, 'Impossible de confirmer les métadonnées.'));
    } finally { setPreviewLoading(false); }
  };

  const toggleTaskPause = async () => {
    try {
      await autosave.flush();
      setPreviewLoading(true);
      const action = preparation.status === 'paused' ? 'resume' : 'pause';
      const { data } = await axios.post(`${Api}/ged/documents/import-zip/tasks/${preparation.preparationId}/${action}`, {
        editRevision: preparationRef.current.editRevision ?? 0,
      }, authHeaders);
      applyPreparation(data, true);
    } catch (error) { showPreviewError(importErrorMessage(error, 'Action impossible.')); }
    finally { setPreviewLoading(false); }
  };

  const refreshPreparation = async () => {
    if (!preparation) return;
    await handlePreview(false, false, null, true);
  };

  const handleImport = async () => {
    setImportRequestError('');
    if (!canImport) return;
    if (!canImportPreparation) {
      showImportError('Préparez le ZIP, attendez la fin de l’analyse puis confirmez les métadonnées avant l’import.');
      return;
    }
    if (duplicateReferences.size > 0 || hasInvalidMetadata(previewItems)) {
      showImportError('Corrigez les références dupliquées et les métadonnées invalides avant import.');
      return;
    }
    const data = new FormData();
    try { await autosave.flush(); } catch (_) { return; }
    data.append('preparationId', preparation.preparationId);
    data.append('revision', preparationRef.current.revision);
    setImporting(true);
    setResult(null);
    setBatchValidation(null);
    try {
      const response = await axios.post(GED_DOCUMENT_IMPORT_ZIP(), data, authHeaders);
      setResult(response.data || null);
      const statusResponse = await axios.get(`${GED_DOCUMENT_IMPORT_ZIP_PREVIEW()}/${preparation.preparationId}`, authHeaders);
      applyPreparation(statusResponse.data);
      setPreparationDirty(false);
      if (response.data?.batchId) {
        navigate(`${location.pathname}?preparationId=${preparation.preparationId}&batchId=${encodeURIComponent(response.data.batchId)}`, { replace: true });
      }
      if ((response.data?.errors || []).length > 0) {
        ToastError((response.data?.imported || 0) > 0
          ? 'Import partiel : consultez les erreurs dans le résultat de l’import.'
          : 'Import échoué : consultez les erreurs dans le résultat de l’import.');
      } else {
        ToastConfirm('Fichiers et métadonnées importés. Les archives sont disponibles en brouillon pour validation.');
      }
    } catch (error) {
      showImportError(importErrorMessage(error, 'Erreur pendant l’import.'));
      // A lost response can follow successful writes. Recover the saved result before another attempt.
      try {
        const response = await axios.get(`${GED_DOCUMENT_IMPORT_ZIP_PREVIEW()}/${preparation.preparationId}`, authHeaders);
        applyPreparation(response.data);
        if (response.data.importResult) {
          setResult(response.data.importResult);
          if (response.data.status === 'imported') setImportRequestError('');
        }
      } catch (_) { /* Keep the original import error visible. */ }
    } finally {
      setImporting(false);
    }
  };

  const handleBatchValidation = async () => {
    const ids = (result?.created || [])
      .filter((item) => item.readyForValidation && item.status === 'draft')
      .map((item) => item.id);
    if (!canValidate || ids.length === 0) return;

    setBulkApproving(true);
    try {
      const response = await axios.post(GED_DOCUMENT_VALIDATE_BATCH(), result.batchId ? { batchId: result.batchId } : { ids }, {
        headers: { Authorization: 'Bearer ' + session.token },
      });
      const payload = response.data || {};
      const validatedIds = new Set(payload.validated || []);
      const skippedById = new Map((payload.skipped || []).map((item) => [item.id, item.reason]));
      setResult((previous) => previous ? {
        ...previous,
        created: previous.created.map((item) => ({
          ...item,
          status: validatedIds.has(item.id) ? 'Actif Conservée' : item.status,
          validationReason: skippedById.get(item.id) || item.validationReason,
        })),
      } : previous);
      setBatchValidation(payload);
      ToastConfirm(`${validatedIds.size} archive${validatedIds.size > 1 ? 's' : ''} validée${validatedIds.size > 1 ? 's' : ''} en lot.`);
    } catch (error) {
      ToastError(error.response?.data?.message || 'Impossible de valider le lot.');
    } finally {
      setBulkApproving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Archives / Import ZIP" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div id="archive-import" className="col-md-8 col-lg-9 col-xl-9 archive-import-page">
              <div className="archive-import-task-header">
                <div className="archive-import-task-heading">
                  <h4 className="htitle mb-0">{preparation ? preparation.title || 'Lot en cours' : 'Nouveau lot d’import'}</h4>
                  {preparation && <span role="status" className={`archive-import-save-status ${autosave.error ? 'text-danger' : 'text-muted'}`}>
                    {autosave.status === 'saving' ? 'Enregistrement en cours…' : autosave.status === 'dirty' ? 'Modifications à enregistrer…' : autosave.status === 'error' ? 'Échec de sauvegarde' : 'Toutes les modifications sont enregistrées'}
                  </span>}
                </div>
                <div className="archive-import-task-actions">
                  {taskEditable && <button type="button" className="btn btn-primary btn-uniform-action archive-import-save-button" disabled={previewLoading || importing || leaving || autosave.saving}
                    onClick={() => autosave.flush().catch(() => {})}><i className="ri-save-line" aria-hidden="true" />Enregistrer maintenant</button>}
                  <button
                    className={`btn btn-uniform-action ${taskEditable ? 'btn-primary archive-import-save-button' : 'btn-outline-secondary'}`}
                    type="button"
                    disabled={leaving || importing}
                    onClick={() => navigationRef.current.leave('/apps/in/archives/taches-incompletes')}
                  >
                    <i className={taskEditable ? 'ri-logout-box-r-line' : 'ri-task-line'} aria-hidden="true" />
                    {taskEditable ? 'Enregistrer et quitter' : 'Tâches incomplètes'}
                  </button>
                </div>
              </div>
              {autosave.error && <div className="alert alert-danger" role="alert">{autosave.error}
                <button type="button" className="btn btn-sm btn-outline-danger ml-2" onClick={async () => {
                  if (!window.confirm('Recharger les valeurs enregistrées remplacera la saisie locale non sauvegardée. Continuer ?')) return;
                  try {
                    const { data } = await axios.get(`${GED_DOCUMENT_IMPORT_ZIP_PREVIEW()}/${preparation.preparationId}`, authHeaders);
                    restorePreparation(data);
                  } catch (error) { showPreviewError(importErrorMessage(error, 'Chargement impossible.')); }
                }}>Recharger le lot enregistré</button>
              </div>}
              <ImportMetadataReport batchId={result?.batchId || queryParams.get('batchId')} token={session.token} canEdit={canImport} canExport={canImport && accessFilters.allowExport !== false} onReport={updateMetadataReport} />

              {!canImport && (
                <div className="alert alert-warning">
                  Vous n'avez pas les droits pour importer des archives.
                </div>
              )}

              <fieldset className="archive-create-form archive-import-edit-fieldset" disabled={previewLoading || importing || leaving || Boolean(preparation && !taskEditable)}>
                <section className="archive-create-section">
                  <div className="archive-create-section-heading">
                    <span className="archive-create-step">1</span>
                    <div><h5>Document</h5><p>Sélectionnez une archive ZIP contenant les documents à importer.</p></div>
                  </div>
                  <div className="archive-create-section-body">
                    <div className="form-group">
                      <label htmlFor="import-lot-title">Intitulé du lot</label>
                      <input id="import-lot-title" className="form-control" maxLength={200} value={lotTitle}
                        onChange={(event) => setLotTitle(event.target.value)} placeholder="Le nom du ZIP sera utilisé par défaut" />
                      {preparation && <small className="text-muted">ZIP original : {preparation.originalFilename || 'archive.zip'} · Démarré le {taskDate(preparation.startedAt)}</small>}
                    </div>
                    <label>Fichier ZIP <span className="archive-create-required">*</span></label>
                    <div {...getRootProps({ className: 'archive-create-dropzone' })}>
                      <input {...getInputProps({ 'aria-label': 'Fichier ZIP à importer' })} />
                      <span className="archive-create-dropzone-icon" aria-hidden="true">↑</span>
                      <strong>Glissez un fichier ZIP ici ou cliquez pour le choisir</strong>
                      <span>Un seul fichier ZIP à la fois</span>
                    </div>
                    {(acceptedFiles.length > 0 || fileRejections.length > 0) && (
                      <ul className="archive-import-files">
                        {acceptedFiles.map((file) => (
                          <li key={file.path}>
                            <span>{file.path}</span><small>{(file.size / 1024 / 1024).toFixed(2)} Mo</small>
                          </li>
                        ))}
                        {fileRejections.map(({ file, errors }) => (
                          <li key={file.path} className="text-danger">
                            {file.path} - {errors.map((e) => e.message).join(', ')}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>

                <section className="archive-create-section">
                  <div className="archive-create-section-heading">
                    <span className="archive-create-step">2</span>
                    <div><h5>Classement</h5><p>Les données du manifeste CSV ou JSON priment sur les valeurs par défaut.</p></div>
                  </div>
                  <div className="archive-create-section-body">

                  <div className="archive-import-classification-grid">
                    <div className="archive-import-classification-field">
                      <div className="form-group">
                        <label>Service producteur</label>
                        <select
                          className="form-control"
                          value={form.producerServiceId}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, producerServiceId: e.target.value }))
                          }
                          disabled={(isAssistantOnly && serviceScopeIds.length === 1) || loadingServices || addingService}
                        >
                          <option value="">
                            {loadingServices ? 'Chargement...' : 'Choisir un service'}
                          </option>
                          {services.filter((service) => !isAssistantOnly || serviceScopeIds.includes(Number(service.id))).map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.label}
                            </option>
                          ))}
                        </select>
                        {isAssistantOnly && (
                          <small className="text-muted">
                            Service associé à votre compte assistant.
                          </small>
                        )}
                        {canAddService && !isAssistantOnly && (
                          <div className="form-check mt-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="import-service-add"
                              checked={addingService}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setAddingService(checked);
                                if (checked) {
                                  setForm((prev) => ({ ...prev, producerServiceId: '' }));
                                } else {
                                  setNewService({ code: '', title: '' });
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="import-service-add">
                              Ajouter un nouveau service
                            </label>
                          </div>
                        )}
                        {addingService && !isAssistantOnly && (
                          <div className="row mt-2">
                            <div className="col-md-4">
                              <input
                                className="form-control"
                                placeholder="Code"
                                value={newService.code}
                                onChange={(e) =>
                                  setNewService({ ...newService, code: e.target.value })
                                }
                              />
                            </div>
                            <div className="col-md-8">
                              <input
                                className="form-control"
                                placeholder="Libellé du service"
                                value={newService.title}
                                onChange={(e) =>
                                  setNewService({ ...newService, title: e.target.value })
                                }
                                required={addingService}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="archive-import-classification-field">
                      <div className="form-group">
                        <label>Plan de classement</label>
                        <select
                          className="form-control"
                          value={form.planId}
                          onChange={(e) => setForm((prev) => ({ ...prev, planId: e.target.value, folderId: '' }))}
                          disabled={loadingPlans || availablePlans.length === 0}
                        >
                          <option value="">
                            {loadingPlans ? 'Chargement...' : availablePlans.length === 0 ? 'Aucun plan actif autorisé' : 'Choisir un plan'}
                          </option>
                          {groupedPlanOptions.map(({ plan, depth }) => {
                            const prefix = depth > 0 ? '-- '.repeat(depth) : '';
                            const label = `${prefix}${plan.code} - ${plan.title}`;
                            return (
                              <option key={plan.id} value={plan.id}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    <div className="archive-import-classification-field archive-import-rule-field">
                      <div className="form-group">
                        <label>Règle de conservation *</label>
                        <select
                          className="form-control"
                          value={form.regleConservationId}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, regleConservationId: e.target.value }))
                          }
                        >
                          <option value="">Choisir une règle</option>
                          {regles.map((regle) => (
                            <option key={regle.id} value={regle.id} disabled={regle.retentionTrigger === 'event' || regles.some((candidate) => Number(candidate.supersedesRuleId) === Number(regle.id))}>
                              {regle.code ? `${regle.code} — ` : ''}{regle.regle}{regle.retentionTrigger === 'event' ? ' — date d’événement requise (dépôt individuel)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="archive-import-structure">
                    <div className="archive-import-folder-picker">
                      <div className="archive-import-folder-choice">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={useFolder}
                          onChange={(e) => setUseFolder(e.target.checked)}
                          id="useFolderSwitch"
                        />
                        <div>
                          <label htmlFor="useFolderSwitch">Archiver dans un dossier</label>
                          <small>Sans manifeste CSV, le plan et le dossier choisis s’appliquent à tous les fichiers. Avec un manifeste, son classement est utilisé.</small>
                        </div>
                      </div>
                      <div className="archive-import-folder-field">
                        <label htmlFor="import-folder">Dossier cible <span>Facultatif</span></label>
                        <select
                          id="import-folder"
                          className="form-control"
                          value={form.folderId}
                          onChange={(e) => setForm((prev) => ({ ...prev, folderId: e.target.value }))}
                          disabled={!useFolder || !form.planId || loadingFolders}
                        >
                          <option value="">
                            {loadingFolders ? 'Chargement...' : 'Choisir un dossier'}
                          </option>
                          {filteredFolders.map((folder) => (
                            <option key={folder.id} value={folder.id}>
                              {folder.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  </div>
                </section>

                <section className="archive-create-section">
                  <div className="archive-create-section-heading">
                    <span className="archive-create-step">3</span>
                    <div><h5>Contrôle et gestion des métadonnées</h5><p>Choisissez le profil, ajoutez des métadonnées et définissez les vérifications avant le versement.</p></div>
                  </div>
                  <div className="archive-create-section-body">
                    <div className="form-group">
                      <label htmlFor="import-archive-profile">Profil d'archive <span className="archive-create-required">*</span></label>
                      <select
                        id="import-archive-profile"
                        className="form-control"
                        value={form.archiveProfileId}
                        onChange={(e) => {
                          const archiveProfileId = e.target.value;
                          const nextProfile = profiles.find(
                            (item) => Number(item.id) === Number(archiveProfileId)
                          );
                          setForm((prev) => ({
                            ...prev,
                            archiveProfileId,
                            producerServiceId:
                              prev.producerServiceId || nextProfile?.producerServiceId || '',
                          }));
                        }}
                      >
                        <option value="">Choisir un profil</option>
                        {profiles.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.code} - {profile.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="archive-import-metadata-defaults">
                      <h3>Métadonnées supplémentaires des fichiers</h3>
                      <p className="text-muted mb-2">
                        Ajoutez des informations propres à cet import, par exemple « Exercice : 2026 ».
                        Chaque métadonnée est ajoutée à tous les fichiers du ZIP et peut être ajustée par fichier lors de la prévisualisation.
                      </p>
                      {metadataDefaults.map((field, index) => (
                        <div className="row align-items-center mb-2" key={`meta-default-${index}`}>
                          <div className="col-md-5">
                            <input
                              className="form-control"
                              placeholder="Nom de la métadonnée"
                              aria-label={`Nom de la métadonnée ${index + 1}`}
                              value={field.key}
                              onChange={(e) =>
                                updateMetadataDefault(index, 'key', e.target.value)
                              }
                            />
                          </div>
                          <div className="col-md-5">
                            <input
                              className="form-control"
                              placeholder="Valeur à ajouter"
                              aria-label={`Valeur de la métadonnée ${index + 1}`}
                              value={field.value}
                              onChange={(e) =>
                                updateMetadataDefault(index, 'value', e.target.value)
                              }
                            />
                          </div>
                          <div className="col-md-2 d-grid">
                            <button
                              className="btn btn-outline-danger btn-sm"
                              type="button"
                              onClick={() => removeMetadataDefault(index)}
                              disabled={metadataDefaults.length === 1}
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        className="btn btn-outline-primary btn-sm"
                        type="button"
                        onClick={addMetadataDefault}
                      >
                        Ajouter une métadonnée
                      </button>
                    </div>

                    <div className="archive-import-checks">
                      <label><input type="checkbox" checked={completeMetadata} onChange={(e) => setCompleteMetadata(e.target.checked)} /> Compléter les métadonnées vides depuis les documents</label>
                      <label><input type="checkbox" checked={verifyMetadata} disabled={!completeMetadata} onChange={(e) => setVerifyMetadata(e.target.checked)} /> Comparer aussi les valeurs du CSV au contenu des documents</label>
                      <small>L’analyse prépare les valeurs avant l’import. Vérifiez les propositions, corrigez les champs obligatoires puis confirmez les métadonnées.</small>
                    </div>

                  <div className="archive-create-actions archive-import-actions">
                      <button
                        className="btn btn-primary archive-import-action-button"
                        type="button"
                        onClick={() => handlePreview()}
                        disabled={!canImport || preparationLocked || (!zipFile && !preparation)}
                      >
                        {previewLoading ? 'Préparation du ZIP...' : preparation ? 'Révalider le mapping' : 'Préparer et prévisualiser'}
                      </button>
                      <button
                        className="btn btn-primary archive-import-action-button"
                        type="button"
                        onClick={handleImport}
                        disabled={!canImport || !canImportPreparation}
                      >
                        {importing ? 'Import en cours...' : "Importer le lot vérifié"}
                      </button>
                  </div>
                  </div>
                </section>
              </fieldset>

              {previewLoading && (
                <div className="archive-loading">{SimpleSpinner()}</div>
              )}

              {previewRequestError && (
                <div className="alert alert-danger mt-3" role="alert">
                  <strong>Erreur de prévisualisation</strong>
                  <div>{previewRequestError}</div>
                </div>
              )}


              {previewErrors.length > 0 && (
                <div className="alert alert-warning mt-3">
                  <div className="fw-bold mb-2">Erreurs du manifeste</div>
                  <ul className="mb-0">
                    {previewErrors.map((error, index) => (
                      <li key={`${error.file || 'err'}-${index}`}>
                        {error.file ? `${error.file} : ` : ''}
                        {error.message || 'Erreur inconnue'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(previewItems.length > 0 || preparation) && (
                <div className="card mt-4 archive-import-review-card">
                  <div className="card-body">
                    {preparation && (
                      <section className="archive-import-preparation" aria-label="Préparation du lot">
                        <h5>Préparation de l’import, prévisualisation et correspondance</h5>
                        <div className="import-task-progress-grid">
                          <div><p>Analyse : {preparation.progress?.analysisDone ?? preparation.count - preparationPending}/{preparation.count} fichiers traités</p>
                            <progress max={preparation.count || 1} value={preparation.progress?.analysisDone ?? preparation.count - preparationPending} aria-label="Progression de l’analyse" /></div>
                          <div><p>Vérification : {preparation.progress?.verified || 0}/{preparation.count} fichiers confirmés</p>
                            <progress max={preparation.count || 1} value={preparation.progress?.verified || 0} aria-label="Progression de la vérification" /></div>
                        </div>
                        <p role="status">{preparation.counts?.review || 0} à vérifier · {preparation.counts?.failed || 0} en échec · {preparation.counts?.blocked || 0} mapping(s) à corriger · {preparation.progress?.imported || 0} déjà importé(s)</p>
                        {preparationPending > 0 && preparation.status === 'preparing' && <p>La lecture se poursuit en arrière-plan. Retrouvez ce lot dans « Tâches incomplètes » à tout moment.</p>}
                        {preparation.status === 'paused' && <p>L’analyse est en pause. Les fichiers déjà analysés restent consultables et modifiables.</p>}
                        {!preparationPending && preparation.status === 'preparing' && <p>
                          {canImportPreparation ? 'Le lot est prêt à être importé.' : <>Vérifiez les valeurs ci-dessous puis cliquez sur <strong>« Confirmer les métadonnées »</strong>. Les champs obligatoires doivent être renseignés.</>}
                        </p>}
                        {preparation.status === 'importing' && <p>Import en cours. Actualisez pour récupérer son résultat avant toute nouvelle tentative.</p>}
                        {preparation.status === 'imported' && <p>Cette préparation a été utilisée pour l’import. Le résultat est affiché ci-dessous.</p>}
                        {preparation.importInterrupted && <p className="text-warning">L’import précédent a été interrompu. Les fichiers déjà importés sont conservés ; seule la suite du lot sera traitée.</p>}
                        {preparation.recoveryRequired && <p className="text-danger">Cet ancien import interrompu doit être rapproché des archives déjà créées par un administrateur avant de poursuivre.</p>}
                        {Object.keys(preparation.draft?.options || {}).length > 0 && <p className="archive-import-warning-text">Des changements de classement sont sauvegardés. Cliquez sur <strong>« Révalider le mapping »</strong> pour les appliquer.</p>}

                      </section>
                    )}
                    <div className="archive-import-review-header">
                      <div className="archive-import-review-toolbar">
                        {preparation && (
                          <div className="archive-import-review-analysis-actions">
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={refreshPreparation}
                              disabled={preparationLocked}>Actualiser l’analyse</button>
                            {taskEditable && <button type="button" className="btn btn-outline-secondary btn-sm" disabled={preparationLocked || autosave.saving}
                              onClick={toggleTaskPause}>{preparation.status === 'paused' ? 'Reprendre l’analyse' : 'Mettre l’analyse en pause'}</button>}
                            {(preparation.counts?.failed || 0) > 0 && taskEditable && <button type="button" className="btn btn-outline-primary btn-sm"
                              disabled={preparationLocked} onClick={() => handlePreview(false, true)}>Relancer les lectures en échec</button>}
                          </div>
                        )}
                        <div className="archive-import-review-csv-actions">
                          <button
                            className="btn btn-outline-secondary btn-sm btn-file-action"
                            type="button"
                            onClick={handleExportMappingCsv}
                            disabled={previewItems.length === 0 || preparationLocked}
                          >
                            Exporter le manifeste CSV
                          </button>
                          <input
                            ref={csvInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="d-none"
                            onChange={handleImportMappingCsv}
                          />
                          <button
                            className="btn btn-outline-secondary btn-sm btn-file-action"
                            type="button"
                            onClick={() => csvInputRef.current?.click()}
                            disabled={previewItems.length === 0 || preparationLocked}
                          >
                            Importer le CSV
                          </button>
                        </div>
                        <div className="archive-import-review-display-options">
                          <label className="archive-import-review-option" htmlFor="showOcrSwitch">
                            <input
                              type="checkbox"
                              checked={showOcr}
                              onChange={(e) => setShowOcr(e.target.checked)}
                              id="showOcrSwitch"
                            />
                            <span>Afficher métadonnées</span>
                          </label>
                          <label className="archive-import-review-option" htmlFor="showMetadataSwitch">
                            <input
                              type="checkbox"
                              checked={showMetadata}
                              onChange={(e) => setShowMetadata(e.target.checked)}
                              id="showMetadataSwitch"
                            />
                            <span>Éditer le JSON des métadonnées</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle syidocs-data-table">
                        <thead>
                          <tr>
                            <th><input type="checkbox" aria-label="Sélectionner les fichiers disponibles" disabled={preparationLocked}
                              checked={previewItems.some((item) => !item.imported && !['queued', 'processing'].includes(item.captureStatus)) && previewItems.filter((item) => !item.imported && !['queued', 'processing'].includes(item.captureStatus)).every((item) => selectedFiles.includes(item.file))}
                              onChange={(event) => setSelectedFiles(event.target.checked ? previewItems.filter((item) => !item.imported && !['queued', 'processing'].includes(item.captureStatus)).map((item) => item.file) : [])} /></th>
                            <th>Fichier</th>
                            <th>Titre</th>
                            <th>Référence</th>
                            <th>Plan</th>
                            {(useFolder || previewItems.some((item) => item.folderId)) && <th>Dossier</th>}
                            <th>Profil d’archive</th>
                            {showOcr && <th>Métadonnées</th>}
                            {showMetadata && <th>JSON des métadonnées</th>}
                            <th>État</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewItems.map((item, index) => {
                            const rowLocked = preparationLocked || Boolean(item.imported) || ['queued', 'processing'].includes(item.captureStatus);
                            const rowFolders = foldersByPlan[item.planId] || [];
                            const rowErrors = [
                              ...(item.errors || []),
                              ...(item.metadataError ? [item.metadataError] : []),
                            ];
                            const previewMetadata = item.metadata && Object.keys(item.metadata).length > 0
                              ? item.metadata
                              : item.ocrMetadata;
                            if (item.reference && duplicateReferences.has(item.reference.trim())) {
                              rowErrors.push('Référence dupliquée dans l\'import');
                            }
                            return (
                              <tr key={`${item.file}-${index}`} className={rowErrors.length ? 'table-danger' : ''}>
                                <td><input type="checkbox" aria-label={`Sélectionner ${item.file}`} disabled={rowLocked} checked={selectedFiles.includes(item.file)}
                                  onChange={(event) => setSelectedFiles((previous) => event.target.checked ? [...previous, item.file] : previous.filter((file) => file !== item.file))} /></td>
                                <td className="text-nowrap">{item.file}</td>
                                <td className="archive-title-cell">
                                  {item.title && item.title.trim() !== '' ? (
                                    <input
                                      className="form-control form-control-sm archive-title-input"
                                      value={item.title || ''}
                                      disabled={rowLocked}
                                      onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                                    />
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  <input
                                    className="form-control form-control-sm"
                                    value={item.reference || ''}
                                    disabled={rowLocked}
                                    placeholder="AUTO"
                                    onChange={(e) => handleItemChange(index, 'reference', e.target.value)}
                                  />
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.planId || ''}
                                    disabled={rowLocked}
                                    onChange={(e) => handleItemChange(index, 'planId', e.target.value)}
                                  >
                                    <option value="">Choisir</option>
                                    {groupedPlanOptions.map(({ plan, depth }) => {
                                      const prefix = depth > 0 ? '-- '.repeat(depth) : '';
                                      const label = `${prefix}${plan.code} - ${plan.title}`;
                                      return (
                                        <option key={plan.id} value={plan.id}>
                                          {label}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </td>
                                {(useFolder || previewItems.some((row) => row.folderId)) && (
                                  <td>
                                    <select
                                      className="form-select form-select-sm"
                                      value={item.folderId || ''}
                                      onChange={(e) => handleItemChange(index, 'folderId', e.target.value)}
                                      disabled={!item.planId || rowLocked}
                                    >
                                      <option value="">Choisir</option>
                                      {rowFolders.map((folder) => (
                                        <option key={folder.id} value={folder.id}>
                                          {folder.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                )}
                                <td>
                                  <select className="form-select form-select-sm" aria-label={`Profil de ${item.file}`}
                                    value={item.archiveProfileId || ''} disabled={rowLocked}
                                    onChange={(e) => handleItemChange(index, 'archiveProfileId', e.target.value)}>
                                    <option value="">Choisir</option>
                                    {profiles.map((archiveProfile) => <option key={archiveProfile.id} value={archiveProfile.id}>{archiveProfile.title || archiveProfile.code}</option>)}
                                  </select>
                                </td>
                                {showOcr && (
                                  <td className="archive-ocr-cell">
                                    {preparation ? <ImportPreparationFields item={item} index={index} disabled={rowLocked}
                                      onChange={(key, value) => updatePreparedMetadata(index, key, value)} /> : renderOcrMetadata(previewMetadata)}
                                  </td>
                                )}
                                {showMetadata && (
                                  <td className="archive-metadata-cell">
                                    <textarea
                                      className="form-control form-control-sm archive-metadata-input"
                                      rows={2}
                                      value={item.metadataJson || ''}
                                      disabled={rowLocked}
                                      onChange={(e) => handleItemChange(index, 'metadataJson', e.target.value)}
                                    />
                                  </td>
                                )}
                                <td className="text-wrap">
                                  {item.imported ? <strong>Importé</strong> : item.reviewed ? <strong className="text-success">Vérifié</strong> : <strong>À vérifier</strong>}
                                  {item.reviewedAt && item.reviewed && <small className="d-block">Confirmé le {taskDate(item.reviewedAt)}</small>}
                                  {rowErrors.length > 0 ? rowErrors.join(', ') : preparationStatusLabels[item.captureStatus] || 'Prévalidation réussie'}
                                  {item.importError && <small className="d-block text-danger">{item.importError}</small>}
                                  {Object.keys(item.metadataWarnings || {}).length > 0 && <small className="d-block text-danger">Métadonnées à compléter ou vérifier : {Object.entries(item.metadataWarnings).map(([key, message]) => `${key} : ${message}`).join(', ')}</small>}
                                  {!item.imported && <button type="button" className="btn btn-sm btn-outline-primary mt-2" disabled={rowLocked}
                                    onClick={() => confirmTaskFiles([item.file])} aria-label={`Confirmer ${item.file}`}>Confirmer les valeurs</button>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="archive-import-actions archive-import-review-actions">
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={() => confirmTaskFiles(previewItems.filter((item) => !item.imported).map((item) => item.file))}
                        disabled={!canImport || preparationLocked || preparationPending > 0}
                      >
                        Confirmer les métadonnées
                      </button>
                      <button type="button" className="btn btn-outline-primary" onClick={() => confirmTaskFiles(selectedFiles)}
                        disabled={!canImport || preparationLocked || selectedFiles.length === 0}>Confirmer la sélection ({selectedFiles.length})</button>
                      <button
                        className="btn btn-primary btn-file-action"
                        type="button"
                        onClick={handleImport}
                        disabled={!canImport || !canImportPreparation}
                      >
                        Importer avec mapping
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {importing && (
                <div className="archive-loading">{SimpleSpinner()}</div>
              )}

              {importRequestError && (
                <div className="card mt-4 archive-import-result-card" role="alert">
                  <div className="card-body">
                    <h5>Résultat de l'import</h5>
                    <div className="alert alert-danger mb-0">{importRequestError}</div>
                  </div>
                </div>
              )}

              {result && (
                <div className="card mt-4 archive-import-result-card">
                  <div className="card-body">
                    <h5>Résultat de l'import</h5>
                    <p className="mb-2">
                      {result.imported || 0} / {result.count || 0} fichiers importés.
                    </p>
                    {result.batchId && <p className="mb-2">Lot ZIP : <code>{result.batchId}</code></p>}
                    {!canValidate && (result.created?.length || 0) > 0 && (
                      <div className="alert alert-info">Un archiviste ou un administrateur peut valider ces archives en lot depuis la liste, en filtrant les brouillons.</div>
                    )}
                    {canValidate && Array.isArray(result.created) && result.created.some((item) => item.readyForValidation && item.status === 'draft') && (
                      <div className="archive-batch-validation">
                        <div>
                          <strong>Validation du lot</strong>
                          <p className="mb-0">Après vérification du rapport, validez ensemble les archives qui possèdent un fichier et les métadonnées obligatoires. Les archives importées non conformes restent en brouillon ; les fichiers rejetés figurent dans les erreurs.</p>
                        </div>
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={handleBatchValidation}
                          disabled={bulkApproving}
                        >
                          {bulkApproving ? 'Validation en cours...' : `Valider en lot (${result.created.filter((item) => item.readyForValidation && item.status === 'draft').length})`}
                        </button>
                      </div>
                    )}
                    {batchValidation && (
                      <div className="alert alert-info mt-3 mb-2">
                        {batchValidation.validated?.length || 0} archive(s) validée(s).
                        {(batchValidation.skipped?.length || 0) > 0 && ` ${batchValidation.skipped.length} archive(s) à corriger.`}
                      </div>
                    )}
                    {Array.isArray(result.errors) && result.errors.length > 0 && (
                      <div className="alert alert-warning">
                        <div className="fw-bold mb-2">Erreurs</div>
                        <ul className="mb-0">
                          {result.errors.map((error, index) => (
                            <li key={`${error.file || 'err'}-${index}`}>
                              {error.file ? `${error.file} : ` : ''}
                              {error.message || 'Erreur inconnue'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(result.created) && result.created.length > 0 && (
                      <div className="table-responsive">
                        <table className="table table-sm table-striped syidocs-data-table">
                          <thead>
                            <tr>
                              <th>Fichier</th>
                              <th>Référence</th>
                              <th>Validation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.created.map((item) => (
                              <tr key={`${item.file}-${item.id}`}>
                                <td>{item.file}</td>
                                <td>{item.reference}</td>
                                <td>
                                  {item.status === 'Actif Conservée' ? 'Validée' : 'Brouillon'}
                                  {item.status === 'draft' && !item.readyForValidation && <span className="archive-import-review-note">{(item.validationIssues || []).join(' · ') || 'Contrôle à compléter'}</span>}
                                  {item.validationReason && <span className="archive-import-review-note">{item.validationReason}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button className="btn btn-outline-primary mt-3" type="button" onClick={() => navigate(result.batchId ? `/apps/in/archives?batchId=${encodeURIComponent(result.batchId)}` : '/apps/in/archives')}>
                      Voir ce lot dans les archives
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ArchiveImport;
